import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useGetDestinationTimestampsQuery } from "src/common/redux/api/exopenApi.js";
import { useGetAllIntegrationsQuery } from "src/common/service/fusionHub/fusionHubApi.js";
import { useGetIntegrationsQuery } from "src/common/service/nexus/nexusApi.js";
import {
	mergeDataSources,
	scopedToLegalEntity,
} from "src/features/integrations/overview/table/mergeDataSources.js";
import { useDummyDisplayTypes } from "src/features/integrations/sources/dummy/dummyDisplayType.js";
import { useActiveJobs } from "src/features/integrations/status/useActiveJobs.js";
import {
	useLoadedCompanyDomain,
	useLoadedLegalEntities,
} from "src/routes/routeDataHooks.js";
import { IntegrationStatusWidgetView } from "./IntegrationStatusWidgetView.js";
import { toStatusRows } from "./summarizeIntegrationStatus.js";
import { usePollingBackoff } from "src/common/utils/hooks/usePollingBackoff.js";

const ROWS_SHOWN = 5;

// WHY 10 s: same cadence as the data-sources table, so the two never disagree for long.
const STATUS_POLL_MS = 10_000;

export const IntegrationStatusWidget = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	const { legalEntityId } = useParams();

	const [integrationsPollMs, backOffOnError] =
		usePollingBackoff(STATUS_POLL_MS);
	const {
		data: fusionHubIntegrations,
		isLoading: isLoadingFusionHub,
		isError: isFusionHubError,
	} = useGetAllIntegrationsQuery(
		{ companyDomainId: companyDomain.id },
		{ pollingInterval: integrationsPollMs },
	);
	backOffOnError(isFusionHubError);
	const {
		data: nexusIntegrations,
		isLoading: isLoadingNexus,
		isError: isNexusError,
	} = useGetIntegrationsQuery({ companyDomainId: companyDomain.id });
	// WHY gate on timestamps loading but not error: rows would briefly show stale sync times
	const { data: destinationTimestamps, isLoading: isLoadingTimestamps } =
		useGetDestinationTimestampsQuery(
			{ companyDomainId: companyDomain.id },
			{ pollingInterval: STATUS_POLL_MS },
		);

	const activeJobs = useActiveJobs(companyDomain.id, STATUS_POLL_MS);
	const dummyDisplayTypes = useDummyDisplayTypes(fusionHubIntegrations?.items);

	const dataSources =
		fusionHubIntegrations && nexusIntegrations
			? mergeDataSources({
					legalEntities,
					fusionHubIntegrations: fusionHubIntegrations.items,
					nexusIntegrations,
					destinationTimestamps: destinationTimestamps ?? [],
					activeJobs,
					dummyDisplayTypes,
				})
			: undefined;

	const scopedDataSources =
		dataSources && scopedToLegalEntity(dataSources, legalEntityId);

	const rows = scopedDataSources
		? toStatusRows(scopedDataSources, ROWS_SHOWN)
		: undefined;

	return (
		<IntegrationStatusWidgetView
			isLoading={isLoadingFusionHub || isLoadingNexus || isLoadingTimestamps}
			isError={isFusionHubError || isNexusError}
			rows={rows}
			action={
				<Link to="data-sources" className="hover:underline">
					{t("See all")} →
				</Link>
			}
		/>
	);
};
